import { PAYMENT_PROVIDER_METHODS, type PaymentMethod } from '@brechoflow/contracts';
import { isApiError } from '@/lib/api';
import { THEME_PRESETS } from '@/features/site/public/theme';
import type { PublicTheme } from '@/features/site/public/types';

/** Tema padrão do projeto, usado quando a loja ainda não publicou o site (404). */
export const DEFAULT_STORE_THEME: PublicTheme = {
  primary: THEME_PRESETS[0]!.primary,
  accent: THEME_PRESETS[0]!.accent,
  background: THEME_PRESETS[0]!.background,
  fontPair: 'classic',
  logoUrl: null,
  faviconUrl: null,
};

/** Meios que exigem o CPF do pagador (provedor). */
export function needsPayerCpf(method: PaymentMethod): boolean {
  return (PAYMENT_PROVIDER_METHODS as readonly string[]).includes(method) || method === 'debit_card';
}

/** Meios cujo pagamento acontece numa página hospedada do provedor (nunca pedimos dados de cartão). */
export const HOSTED_METHODS: readonly PaymentMethod[] = ['credit_card', 'debit_card', 'boleto', 'payment_link'];

/** Só abre links http(s) vindos do provedor. */
export function safeExternalUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  if (d > 0) return `${d}d ${p(h)}:${p(m)}:${p(s)}`;
  if (h > 0) return `${p(h)}:${p(m)}:${p(s)}`;
  return `${p(m)}:${p(s)}`;
}

export interface Issue {
  path: string;
  message: string;
}

export const issuesOf = (e: unknown): Issue[] => (isApiError(e) ? e.issues : []);

export function itemMeta(i: { sizeLabel: string | null; brandName: string | null }): string {
  return [i.sizeLabel ? `Tam. ${i.sizeLabel}` : null, i.brandName].filter(Boolean).join(' · ');
}

/** QR em base64: aceita com ou sem o prefixo data:. */
export function qrSrc(b64: string): string {
  return b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
}

export const onlyDigits = (v: string) => v.replace(/\D/g, '');
